import type { CasinoSettingsData, LogoVariant } from './types';

/**
 * Mock data for the Casino Settings page.
 *
 * Temporary: exists only while the page is built ahead of its APIs. Two things
 * here have no endpoint yet: the ML-generated logo variant set (the schematic
 * fallback art) and the casino-type setting. The logo-edit window (the 50-player
 * gate on replacing a logo) is NO LONGER mocked: it is real, computed by
 * `pages/casino-settings/logos.computeLogoEditWindow` from the brand's
 * registered-player count. The domain list is real too (the
 * `/casino-instance/domain-info` API + `resolveCasinoDomains`), with an honest
 * "unknown" state for brands that have no record. When the remaining endpoints
 * land, swap `buildCasinoSettings` for a selector yielding the same
 * `CasinoSettingsData` shape and delete this file. Static UI copy (section titles,
 * button labels, descriptions) is NOT here — it lives in the components, per the
 * area conventions.
 *
 * The page maps the active brand to one of the `CASINO_PROFILES` below (cycling by
 * its position in the brand list, exactly like the Home dashboard) only for the
 * still-mocked casino type and name fallback; the logo lock now reflects the real
 * active brand.
 */

/**
 * The variant set the ML system produces from the owner's primary logo. The same
 * lockups apply to every casino (only the wordmark/name differs), so the template
 * is shared; the page draws each as a schematic preview at its native aspect ratio.
 */
const LOGO_VARIANTS: LogoVariant[] = [
  { id: 'primary', label: 'Primary logo', ratio: '16:5', width: 640, height: 200, layout: 'horizontal', background: 'light', primary: true },
  { id: 'horizontal-dark', label: 'Horizontal · dark', ratio: '16:5', width: 640, height: 200, layout: 'horizontal', background: 'dark' },
  { id: 'stacked', label: 'Stacked', ratio: '4:3', width: 480, height: 360, layout: 'stacked', background: 'light' },
  { id: 'symbol', label: 'Symbol', ratio: '1:1', width: 320, height: 320, layout: 'symbol', background: 'light' },
  { id: 'wordmark', label: 'Wordmark', ratio: '16:4', width: 640, height: 160, layout: 'wordmark', background: 'light' },
  { id: 'monochrome', label: 'Monochrome', ratio: '16:5', width: 640, height: 200, layout: 'horizontal', background: 'dark', mono: true },
  { id: 'favicon', label: 'Favicon', ratio: '1:1', width: 128, height: 128, layout: 'symbol', background: 'light' },
];

type CasinoProfile = Omit<CasinoSettingsData, 'logos'>;

/**
 * Stand-in casinos, each carrying a casino type and name fallback for the
 * still-mocked casino-type section; cycling brands walks through every type. (The
 * logo lock is no longer mocked here: it is real, see `computeLogoEditWindow`; the
 * domain list is real too, see `resolveCasinoDomains`.)
 */
const CASINO_PROFILES: readonly CasinoProfile[] = [
  { casinoName: 'LuckySpin', kind: 'mixed' },
  { casinoName: 'CryptoReels', kind: 'crypto' },
  { casinoName: 'RoyalBet', kind: 'fiat' },
  { casinoName: 'SpinCity', kind: 'mixed' },
];

/** Number of stand-in casinos — the page cycles the active brand through these. */
export const CASINO_VARIANT_COUNT = CASINO_PROFILES.length;

export type CasinoVariant = number;

/** Build the Casino Settings dataset for a brand variant (its index in the list, cycling). */
export function buildCasinoSettings(variant: CasinoVariant = 0): CasinoSettingsData {
  const profile = CASINO_PROFILES[((variant % CASINO_VARIANT_COUNT) + CASINO_VARIANT_COUNT) % CASINO_VARIANT_COUNT];
  return {
    casinoName: profile.casinoName,
    logos: { variants: LOGO_VARIANTS },
    kind: profile.kind,
  };
}

export default buildCasinoSettings;
